'use client'

import { useId, useState } from 'react'
import { Check } from 'lucide-react'

import type { LandingMessages } from '@/lib/marketing/landing-messages'
import { cn } from '@/lib/utils'
import { CtaLink } from './marketing-parts'

export type PricingTier = {
  id: string
  name: string
  /** USD per month; null for Enterprise (custom). */
  price: number | null
  /** Verified members included; null above the last published plan. */
  memberLimit: number | null
  bestFor: string
  cta: { label: string; href: string }
}

type Copy = LandingMessages['pricing']['calculator']

/** "$0.40": the monthly price per verified member at the plan's ceiling. */
export function perMember(tier: PricingTier): string | null {
  if (tier.price === null || tier.memberLimit === null || tier.price === 0) return null
  return `$${(Math.round((tier.price / tier.memberLimit) * 100) / 100).toFixed(2)}`
}

/** How much less a member costs than on Starter, rounded like the published table. */
function savingsVs(tier: PricingTier, starter: PricingTier | undefined): string | null {
  if (!starter || tier.id === starter.id || tier.price === null || tier.memberLimit === null)
    return null
  if (starter.price === null || starter.memberLimit === null || tier.price === 0) return null
  const pct = Math.round(
    (1 - tier.price / tier.memberLimit / (starter.price / starter.memberLimit)) * 100
  )
  return pct > 0 ? `${pct}%` : null
}

/**
 * "How many members do you expect?": a slider over the published plans and
 * the plan it lands on. Native `input type="range"` (keyboard and screen
 * readers for free); the tick labels are buttons too.
 */
export function PricingCalculator({
  tiers,
  copy,
  numberLocale,
  perMonth,
}: {
  tiers: PricingTier[]
  copy: Copy
  /** "/ month", from the pricing teaser's copy. */
  perMonth: string
  /** BCP 47 locale for member counts ("1,000" / "1.000"). */
  numberLocale: string
}) {
  const id = useId()
  const [index, setIndex] = useState(0)
  const tier = tiers[index] ?? tiers[0]
  const starter = tiers.find((t) => t.id === 'starter')
  const count = (n: number) => n.toLocaleString(numberLocale)
  const unit = perMember(tier)
  const savings = savingsVs(tier, starter)
  const priceLabel =
    tier.price === null ? copy.custom : tier.price === 0 ? copy.free : `$${count(tier.price)}`

  return (
    <div className="grid items-start gap-8 lg:grid-cols-12">
      <div className="grid gap-6 lg:col-span-7">
        <div className="grid gap-4 rounded-en-lg bg-en-surface p-6 shadow-en-card md:p-8">
          <div className="grid gap-1">
            <label htmlFor={`${id}-range`} className="text-en-subheading text-en-ink">
              {copy.howManyMembers}
            </label>
            <p className="text-en-meta text-en-ink-muted">{copy.verifyHint}</p>
          </div>
          <p className="flex items-baseline gap-2" aria-live="polite">
            <span className="text-4xl font-semibold tracking-tight text-en-brand">
              {tier.memberLimit === null
                ? `${count(tiers[tiers.length - 2]?.memberLimit ?? 7500)}+`
                : count(tier.memberLimit)}
            </span>
            <span className="text-en-body text-en-ink-muted">{copy.members}</span>
          </p>
          <input
            id={`${id}-range`}
            type="range"
            min={0}
            max={tiers.length - 1}
            step={1}
            value={index}
            onChange={(e) => setIndex(Number(e.target.value))}
            aria-valuetext={`${tier.name}, ${
              tier.memberLimit === null ? copy.customLimit : count(tier.memberLimit)
            } ${copy.members}`}
            className="h-11 w-full cursor-pointer accent-en-electric focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus"
          />
          <ol className="grid grid-cols-3 gap-2 sm:grid-cols-6">
            {tiers.map((t, i) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-pressed={i === index}
                  className={cn(
                    'grid min-h-en-control w-full cursor-pointer justify-items-center rounded-en-md px-1 py-1 text-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus',
                    i === index
                      ? 'bg-en-electric-soft text-en-electric'
                      : 'text-en-ink-muted hover:bg-en-surface-sunken'
                  )}
                >
                  <span className="text-en-label font-semibold">{t.name}</span>
                  <span className="text-en-caption font-normal">
                    {t.memberLimit === null ? copy.custom : count(t.memberLimit)}
                  </span>
                </button>
              </li>
            ))}
          </ol>
          <dl className="grid grid-cols-2 gap-3">
            <div className="rounded-en-md bg-en-canvas p-4">
              <dt className="text-en-meta text-en-ink-muted">{copy.plan}</dt>
              <dd className="text-en-body-strong text-en-ink">{tier.name}</dd>
            </div>
            <div className="rounded-en-md bg-en-canvas p-4">
              <dt className="text-en-meta text-en-ink-muted">{copy.pricePerMember}</dt>
              <dd className="text-en-body-strong text-en-ink">
                {tier.price === null ? copy.contactUs : (unit ?? copy.free)}
              </dd>
            </div>
          </dl>
        </div>
        <ul className="grid gap-4">
          {[
            [copy.transparentPricing, copy.transparentPricingDesc],
            [copy.fullAccess, copy.fullAccessDesc],
          ].map(([title, desc]) => (
            <li key={title} className="flex items-start gap-3">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-en-md bg-en-electric-soft text-en-electric">
                <Check aria-hidden="true" className="size-4" />
              </span>
              <div className="grid">
                <p className="text-en-body-strong text-en-ink">{title}</p>
                <p className="text-en-meta text-en-ink-muted">{desc}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="grid gap-5 rounded-en-lg bg-en-surface p-6 shadow-en-overlay ring-2 ring-en-ink md:p-8 lg:col-span-5">
        <div className="grid gap-2">
          <p className="text-en-label font-semibold text-en-electric">{tier.bestFor}</p>
          <h3 className="text-en-title text-en-ink">{tier.name}</h3>
          <p className="flex items-baseline gap-1">
            <span className="text-5xl font-semibold tracking-tight text-en-ink">{priceLabel}</span>
            {tier.price ? <span className="text-en-body text-en-ink-muted">{perMonth}</span> : null}
          </p>
          {savings ? (
            <p className="justify-self-start rounded-en-full bg-en-positive-soft px-3 py-1 text-en-label font-semibold text-en-positive">
              {copy.savingsVsStarter.replace('{savings}', savings)}
            </p>
          ) : null}
        </div>
        <div className="grid gap-3 border-t border-en-line pt-5">
          <p className="text-en-body-strong text-en-ink">{copy.allCoreFeatures}</p>
          <ul className="grid gap-2 text-en-meta text-en-ink">
            {[
              tier.memberLimit === null
                ? copy.customLimit
                : copy.upToMembers.replace('{count}', count(tier.memberLimit)),
              copy.fullEngagementSuite,
              copy.adminDashboard,
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <Check
                  aria-hidden="true"
                  strokeWidth={2.5}
                  className="mt-1 size-4 shrink-0 text-en-electric"
                />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <CtaLink href={tier.cta.href} arrow className="w-full">
          {tier.cta.label}
        </CtaLink>
        <p className="text-center text-en-meta text-en-ink-muted">
          {tier.price === 0
            ? copy.noCreditCard
            : tier.price === null
              ? copy.talkToEnterprise
              : copy.startFreeUpgrade}
        </p>
      </div>
    </div>
  )
}
